// Crafting bench recipes: data only, plus the checks the server and the Bag
// page share. The bench is a sink for surplus drops and coins, never a cheap
// source of tradeable rares:
// - Boss keys and the Dragon Pearl
//   come from many spare drops plus a large coin fee, far worse than finding
//   one, and only a few times a week, so crafting adds well under 10% to their
//   supply (eval bands check it; Yomi is past the week-long eval, so
//   tests/recipes.test.ts checks its weekly caps against Yomi drop rates).
// - Shrine Shards are never an input or an output: shrines stay scarce.
import { SPECIES, type RegionId } from './species.ts';
import { ELEMENTS, ELEMENT_IDS } from './elements.ts';
import { AURA_AWAKENING_SHARD, AWAKENING_SHARD, CREST_TITLE, ITEM, auraAwakeningStone, awakeningStone, tabletItem } from './items.ts';
import { AWAKENINGS } from './awakening-data.ts';
import { TABLET_COINS, TABLET_FRAGMENTS } from './tablets.ts';

export interface Recipe {
  id: string;
  name: string;
  desc: string;
  in: { item: string; qty: number }[];
  /** Coins burned per craft. */
  coins: number;
  out: { item: string; qty: number };
  /** Crafts allowed per account per week (Monday to Sunday, UTC); absent = no limit. */
  perWeek?: number;
  /** The `perWeek` limit covers this many weeks instead of one (fixed blocks of whole weeks, see periodOf). */
  everyWeeks?: number;
  /** Trainer level needed. */
  minLevel?: number;
  region: RegionId;
  /** A trainer title the first craft unlocks for good (worn like pass titles: shared/pass.ts passTitles). */
  title?: string;
}

/**
 * Star items per crafted boss key. A key drops 1 in CONFIG.bossTokenOneIn (4,000) kills
 * and a star item 1 in 250, so 50 star items are 12,500 kills of drops: about three
 * times the cost of finding the key, plus the coin fee.
 */
export const KEY_STAR_ITEMS = 50;
/**
 * One crafted key per boss every four weeks, and only once you hunt a region further on (Hachimata, the last
 * boss, opens where Dragon Pearls drop). An active player finds about five keys a week on the region's last
 * hunts, so crafting stays well under 10% of the keys found (sim/targets.ts: craftKeyShare).
 */
export const KEY_CRAFT_WEEKS = 4;

/** Shards per Awakening Stone (shared/awakening.ts SHARDS_PER_STONE). */
const AWAKEN_SHARDS = 10;

/** Coins burned per Boss Ladder crest. */
export const CREST_COINS = 1_000_000;
/** Dragon Pearls per Sacred Rope: fewer than the other keys' 50 star items, since pearls are the stars-6-and-7 material. */
export const ROPE_PEARLS = 10;
const CREST_NAMES = ['Fenrir', 'Typhon', 'Hachimata'];
const CREST_WHAT = ['wolf-head', 'storm', 'serpent'];
const CREST_REGIONS: RegionId[] = ['midgard', 'hellas', 'yomi'];

export const RECIPES: Recipe[] = [
  { id: 'fenrir-key', name: 'Forge a Chain of Gleipnir', desc: 'Fifty Runes of Ascent bound into a Fenrir key, for spare Midgard runes once you hunt in Hellas. Finding one is far quicker.', in: [{ item: 'rune-of-ascent', qty: KEY_STAR_ITEMS }], coins: 100_000, out: { item: 'fenrir-token', qty: 1 }, perWeek: 1, everyWeeks: KEY_CRAFT_WEEKS, minLevel: 150, region: 'midgard' },
  { id: 'typhon-key', name: 'Forge a Storm Sigil', desc: 'Fifty Olympian Embers bound into a Typhon key, for spare Hellas embers once you hunt in Yomi. Finding one is far quicker.', in: [{ item: 'olympian-ember', qty: KEY_STAR_ITEMS }], coins: 2_000_000, out: { item: 'typhon-token', qty: 1 }, perWeek: 1, everyWeeks: KEY_CRAFT_WEEKS, minLevel: 900, region: 'hellas' },
  // Yomi. A fee costs the same number of kills at the level the recipe opens as its Hellas twin: Ambrosia's 20,000
  // is 27 kills at 150, so Offering Rice is 27 kills at 900 (60,000); the Storm Sigil's 2M is 1,255 kills at 550, so
  // the Sacred Rope is 1,255 kills at 1,700 (4M). The Dragon Pearl, the first star-item recipe, pays a quarter of
  // that. Level gates are where each item starts to drop; pearl crafts are capped at 5 a week.
  { id: 'ember-pearl', name: 'Temper embers into a Dragon Pearl', desc: 'Ten Olympian Embers tempered into one Dragon Pearl, for spare embers once Yomi hunts drop pearls. Finding one is far quicker.', in: [{ item: 'olympian-ember', qty: 10 }], coins: 1_000_000, out: { item: 'dragon-pearl', qty: 1 }, perWeek: 5, minLevel: 1700, region: 'yomi' },
  { id: 'hachimata-key', name: 'Braid a Sacred Rope', desc: 'Ten Dragon Pearls braided into a Hachimata key.', in: [{ item: 'dragon-pearl', qty: ROPE_PEARLS }], coins: 4_000_000, out: { item: 'hachimata-token', qty: 1 }, perWeek: 1, everyWeeks: KEY_CRAFT_WEEKS, minLevel: 1700, region: 'yomi' },
  // Boss Ladder (shared/ladder.ts): ten Crest Shards of a boss and a coin fee make that boss's crest, a trophy that
  // stays on the account, and the first one unlocks the boss's trainer title (CREST_TITLE). The fee is a coin sink,
  // about 200 kills at level 3,000.
  ...(['fenrir', 'typhon', 'hachimata'] as const).map((b, i): Recipe => ({
    id: `${b}-crest`, name: `Set the ${CREST_NAMES[i]} Crest`, desc: `Ten ${CREST_NAMES[i]} Crest Shards from the Boss Ladder set into one ${CREST_WHAT[i]} crest. Crafting it unlocks the trainer title ${CREST_TITLE[b]}.`,
    in: [{ item: `${b}-crest-shard`, qty: 10 }], coins: CREST_COINS, out: { item: `${b}-crest`, qty: 1 }, minLevel: 3000, region: CREST_REGIONS[i], title: CREST_TITLE[b],
  })),
  // Rune Tablets (shared/tablets.ts): ten Tablet Fragments and a coin fee carve the tablet of the element you
  // choose. Tradeable both ways. One of each element a week, far above what the ladder's capped fragment odds supply
  // (about one tablet in four weeks for the busiest climber).
  ...ELEMENT_IDS.map((el): Recipe => ({
    id: `tablet-${el}`, name: `Carve a ${ELEMENTS[el].name} Rune Tablet`,
    desc: `Ten Tablet Fragments carved with the ${ELEMENTS[el].name} rune. Applied on a creature's page, it turns its Coverage move ${ELEMENTS[el].name}: same power, same cooldown.`,
    in: [{ item: 'tablet-fragment', qty: TABLET_FRAGMENTS }], coins: TABLET_COINS, out: { item: tabletItem(el), qty: 1 }, perWeek: 1, region: 'yomi',
  })),
  // Awakening (shared/awakening.ts): ten Awakening Shards make the Awakening Stone of the species you choose, ten Aura
  // Awakening Shards its Aura Awakening Stone. No lottery and no coin fee, as the reference's bench.
  ...AWAKENINGS.flatMap((a): Recipe[] => {
    const sp = SPECIES[a.from];
    return [
      { id: `awaken-${a.from}`, name: `Shape a ${sp.name} Awakening Stone`, desc: `Ten Awakening Shards shaped into the stone that awakens a ${sp.name} into ${a.name}.`, in: [{ item: AWAKENING_SHARD, qty: AWAKEN_SHARDS }], coins: 0, out: { item: awakeningStone(a.from), qty: 1 }, region: sp.region },
      { id: `aura-awaken-${a.from}`, name: `Shape a ${sp.name} Aura Awakening Stone`, desc: `Ten Aura Awakening Shards shaped into the stone that awakens an Aura ${sp.name}.`, in: [{ item: AURA_AWAKENING_SHARD, qty: AWAKEN_SHARDS }], coins: 0, out: { item: auraAwakeningStone(a.from), qty: 1 }, region: sp.region },
    ];
  }),
];

export const RECIPE: Record<string, Recipe> = Object.fromEntries(RECIPES.map((r) => [r.id, r]));

/** Most crafts of one recipe per message. */
export const MAX_CRAFTS = 100;

/** Monday (UTC) of the week holding `ms`, as YYYY-MM-DD: the key for weekly limits. */
export function weekOf(ms: number): string {
  const d = new Date(ms);
  const day = (d.getUTCDay() + 6) % 7;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day)).toISOString().slice(0, 10);
}

/** Start (a Monday, YYYY-MM-DD) of the fixed block of `weeks` whole weeks holding `ms`; blocks count from Monday 1970-01-05. */
export function periodOf(ms: number, weeks: number): string {
  const monday = Date.parse(`${weekOf(ms)}T00:00:00Z`);
  const WEEK = 7 * 86_400_000, EPOCH = Date.UTC(1970, 0, 5);
  const i = Math.floor((monday - EPOCH) / WEEK);
  return new Date(EPOCH + (i - (((i % weeks) + weeks) % weeks)) * WEEK).toISOString().slice(0, 10);
}

/** When the current limit period of a recipe ends (ms). */
export function periodEnds(r: Recipe, now: number): number {
  return Date.parse(`${periodOf(now, r.everyWeeks ?? 1)}T00:00:00Z`) + (r.everyWeeks ?? 1) * 7 * 86_400_000;
}

export type CraftRecord = { week: string; n: Record<string, number> };
export type LongCraftRecord = Record<string, { period: string; n: number }>;

/** Crafts of a recipe in its current limit period (a week, or `everyWeeks` weeks). */
export function craftedInPeriod(t: { crafts?: CraftRecord; craftsLong?: LongCraftRecord }, r: Recipe, now: number): number {
  if ((r.everyWeeks ?? 1) <= 1) return craftedThisWeek(t.crafts, r.id, now);
  const rec = t.craftsLong?.[r.id];
  return rec && rec.period === periodOf(now, r.everyWeeks!) ? rec.n : 0;
}

/** Count crafts against a recipe's limit. */
export function recordCraft(t: { crafts?: CraftRecord; craftsLong?: LongCraftRecord }, r: Recipe, now: number, times: number) {
  if (r.perWeek === undefined) return;
  if ((r.everyWeeks ?? 1) > 1) {
    const period = periodOf(now, r.everyWeeks!);
    t.craftsLong ??= {};
    const rec = t.craftsLong[r.id];
    t.craftsLong[r.id] = { period, n: (rec?.period === period ? rec.n : 0) + times };
    return;
  }
  const week = weekOf(now);
  if (t.crafts?.week !== week) t.crafts = { week, n: {} };
  t.crafts.n[r.id] = (t.crafts.n[r.id] ?? 0) + times;
}

/** The limit in words: "this week" or "in these 4 weeks". */
export function limitPeriodText(r: Recipe): string {
  return (r.everyWeeks ?? 1) > 1 ? `in these ${r.everyWeeks} weeks` : 'this week';
}

/** Crafts done this week, from the trainer's record (an old week counts as none). */
export function craftedThisWeek(crafts: { week: string; n: Record<string, number> } | undefined, recipe: string, now: number): number {
  return crafts && crafts.week === weekOf(now) ? crafts.n[recipe] ?? 0 : 0;
}

/** How many times a trainer can craft a recipe now, and why not when it is 0. */
export function craftable(r: Recipe, t: { level: number; coins: number; bag: Record<string, number>; crafts?: CraftRecord; craftsLong?: LongCraftRecord }, now: number): { n: number; why: string } {
  if (r.minLevel && t.level < r.minLevel) return { n: 0, why: `Needs trainer level ${r.minLevel}` };
  let n = MAX_CRAFTS;
  if (r.perWeek !== undefined) {
    n = Math.min(n, r.perWeek - craftedInPeriod(t, r, now));
    if (n <= 0) return { n: 0, why: `Used ${r.perWeek} of ${r.perWeek} ${limitPeriodText(r)}` };
  }
  for (const x of r.in) n = Math.min(n, Math.floor((t.bag[x.item] ?? 0) / x.qty));
  if (n <= 0) return { n: 0, why: 'Not enough materials' };
  if (r.coins) n = Math.min(n, Math.floor(t.coins / r.coins));
  if (n <= 0) return { n: 0, why: 'Not enough coins' };
  return { n, why: '' };
}
